import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useRef, useState } from 'react';

interface CustomToolTipProps {
    children: React.ReactNode;
    title: React.ReactNode;
    shortcut?: React.ReactNode;
    placement?: 'bottom' | 'top' | 'left' | 'right' |
    'bottom-start' | 'bottom-end' |
    'top-start' | 'top-end' |
    'left-start' | 'left-end' |
    'right-start' | 'right-end';
    disabled?: boolean;
    className?: string;
    svg?: React.ReactNode
    maxWidth?: string
    wordBreak?: React.CSSProperties['wordBreak']
    style?: React.CSSProperties
}

const CustomToolTip: React.FC<CustomToolTipProps> = ({
    children,
    title,
    shortcut,
    placement = 'top',
    disabled = false,
    className,
    svg,
    maxWidth,
    wordBreak = 'normal',
    style,
}) => {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLSpanElement>(null);

    // Utility to find the closest scrollable parent
    const getScrollParent = (element: HTMLElement | null): HTMLElement | null => {
        while (element) {
            const overflowY = window.getComputedStyle(element).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') {
                return element;
            }
            element = element.parentElement;
        }
        return null;
    };

    useEffect(() => {
        const triggerEl = triggerRef.current;
        if (!triggerEl) return;

        const scrollParent = getScrollParent(triggerEl);

        const handleScroll = () => {
            setOpen(false); // Instant close
        };

        const handleClick = () => {
            setOpen(false); // Instant close on click (capture phase)
        };

        scrollParent?.addEventListener('scroll', handleScroll);
        triggerEl.addEventListener('click', handleClick, true);

        return () => {
            scrollParent?.removeEventListener('scroll', handleScroll);
            triggerEl.removeEventListener('click', handleClick, true);
        };
    }, [triggerRef.current]);

    return (
        <Tooltip
            title={
                <div className='d-flex align-items-center' style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    {svg && <span>{svg}</span>}
                    <span style={{
                        wordBreak
                    }}>{title}</span>
                    {shortcut && (
                        <span
                            className="custom-tooltip-shortcut-badge"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0, 0, 0, 0.55)',
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                borderRadius: '4px',
                                padding: '1px 6px',
                                fontSize: '11px',
                                fontWeight: 500,
                                color: '#ffffff',
                                letterSpacing: '0.5px',
                                lineHeight: '16px',
                                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                                userSelect: 'none',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {shortcut}
                        </span>
                    )}
                </div>
            }
            placement={placement}
            open={open && !disabled && (Boolean(title) || Boolean(shortcut))}
            disableInteractive
            disableFocusListener
            disableHoverListener
            leaveDelay={0}
            PopperProps={{
                style: {
                    zIndex: 100000,
                    pointerEvents: 'none',
                },
                modifiers: [
                    {
                        name: "offset",
                        options: {
                            offset: [0, 0], // [skidding, distance]
                            // skidding: horizontal offset, distance: vertical offset
                        },
                    },
                ],
            }}
            componentsProps={{
                tooltip: {
                    sx: {
                        display: disabled ? 'none' : 'flex',
                        color: "#fff",
                        backgroundColor: "rgba(28, 28, 30, 0.94) !important",
                        backdropFilter: "blur(20px)",
                        padding: '4px 8px',
                        fontSize: '12px',
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 400,
                        lineHeight: '16px',
                        letterSpacing: '0.096px',
                        borderRadius: '6px',
                        border: '0.5px solid rgba(255, 255, 255, 0.12)',
                        maxWidth: maxWidth || '700px',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        wordBreak: wordBreak || 'normal',
                        overflow: 'hidden',
                        zIndex: 99999,
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
                    },
                },
            }}
        >
            <span
                className={`${className || ''}`}
                ref={triggerRef}
                style={style}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
            >
                {children}
            </span>
        </Tooltip>
    );
};

export default CustomToolTip;
